import { useCallback, useState } from "react";
import { IControl, IControlProperties } from "../models/control";
import { Form, IForm } from "../models/form";

/**
 * Control properties type excluding name, value, and defaultValue
 * Used for schema definition where these properties are inferred
 */
export type ControlPropertiesWithoutNameAndValue = Omit<
  IControlProperties,
  "name" | "value" | "defaultValue"
>;

/**
 * Control properties type excluding only name
 * Used when value/defaultValue might be specified in schema
 */
export type ControlPropertiesWithoutName = Omit<
  IControlProperties,
  "name" | "value"
>;

/**
 * Schema type extension for array controls
 * Contains nested schema definition for array items
 */
type ArraySchema<T> = { arraySchema: ObjectPropertyIsMapType<T> };

/**
 * Extended control properties that include array schema
 * Used for defining array-type form controls
 */
export type WithArraySchema<T> = ControlPropertiesWithoutNameAndValue &
  ArraySchema<T>;

/**
 * Type guard to check if a schema includes array configuration
 * @param schema - Schema object to check
 * @returns true if schema contains arraySchema property
 */
export function isArraySchema(schema: any) {
  return (schema as WithArraySchema<any>).arraySchema !== undefined;
}

/**
 * Conditional type that determines the appropriate schema type for each property
 * Simple types (string, number, boolean, etc.) use basic control schema
 * Array/object types use array schema with nested structure
 */
export type ControlSchema<T, K extends keyof T> = T[K] extends
  | number
  | string
  | Date
  | undefined
  | null
  | boolean
  | string[]
  | number[]
  | boolean[]
  ? ControlPropertiesWithoutNameAndValue
  : WithArraySchema<Flatten<T[K]>>;

/**
 * Schema for default value settings in form initialization
 */
export type IInitControlDefaultValue = Pick<
  IControlProperties,
  "name" | "defaultValue"
>;

/**
 * Type mapping object properties to their control schemas
 * Each property becomes a control with configuration
 */
export type ObjectPropertyIsMapType<T> = {
  [key in keyof T]: ControlPropertiesWithoutName;
};

/**
 * Utility type to flatten array types for nested schema definition
 */
type Flatten<Type> = Type extends Array<infer Item> ? Item : Type;

/**
 * Complete schema type for a form object
 * Maps each property to its appropriate control schema
 */
export type ObjectSchemaType<T> = {
  [property in keyof T]: ControlSchema<T, property>;
};

/**
 * Utility type to filter only array properties from type T
 * Only includes properties that are arrays (excluding primitive arrays)
 */
export type ArrayPropertiesOf<T> = {
  [K in keyof T]: T[K] extends (infer U)[]
    ? U extends number | string | boolean | Date | null | undefined
      ? never
      : K
    : never;
}[keyof T];

/**
 * Utility type to extract the item type from array properties
 */
export type ArrayItemType<T, K extends keyof T> = T[K] extends (infer U)[]
  ? U
  : never;

/**
 * Typed control interface for array item controls
 * Provides better IntelliSense for accessing control properties
 */
export interface ITypedControl<T> extends Omit<IControl, "getControl"> {
  /**
   * Gets a nested control with proper typing
   * @param fieldName - Name of the field (with IntelliSense support)
   */
  getControl<K extends keyof T>(fieldName: K): IControl;
}

/**
 * Utility type for array control children with proper typing
 */
export type TypedArrayChildren<
  T,
  K extends ArrayPropertiesOf<T>
> = ITypedControl<ArrayItemType<T, K>>[][];

/**
 * Custom React hook for managing dynamic forms
 * Provides form state management, validation, and submission handling
 *
 * @param schema - Form control schema definition (can be static object or factory function)
 * @param defaults - Default values for form fields
 * @returns Object containing form state and control methods with improved TypeScript support
 */
export function useForm<T extends unknown>(
  schema: ObjectSchemaType<T> | ((form: IForm) => ObjectSchemaType<T>),
  defaults: T
) {
  type keyOfT = keyof T;

  // Create form instance with schema and defaults
  const [instanceForm] = useState<IForm>(() => {
    const formStore = Form.create();
    if (typeof schema === "function") {
      // Dynamic schema - pass form instance to schema factory
      formStore.initForm(schema(formStore), defaults);
    } else {
      // Static schema - use directly
      formStore.initForm(schema, defaults);
    }

    return formStore;
  });

  /**
   * Retrieves a specific form control by field name
   * @param fieldName - Name of the field to retrieve
   * @returns The control instance for the specified field
   */
  const getControl = useCallback(
    (fieldName: keyOfT) => {
      return instanceForm.getControl(fieldName as string);
    },
    [instanceForm]
  );

  /**
   * Retrieves the children array for array-type controls with improved typing
   * @param fieldName - Name of the array field (IntelliSense shows only array properties)
   * @returns 2D array of child controls with proper typing for the array items
   */
  const getControlArray = useCallback(
    <K extends ArrayPropertiesOf<T>>(
      fieldName: K
    ): TypedArrayChildren<T, K> => {
      const control = instanceForm.getControl(fieldName as string);
      return control.children as TypedArrayChildren<T, K>;
    },
    [instanceForm]
  );

  /**
   * Gets a specific child control from an array field with proper typing
   * @param fieldName - Name of the array field
   * @param rowIndex - Index of the array item
   * @param itemFieldName - Name of the field within the array item
   * @returns Typed control for the specific array item
   */
  const getArrayItemControl = useCallback(
    <K extends ArrayPropertiesOf<T>>(
      fieldName: K,
      rowIndex: number,
      itemFieldName: keyof ArrayItemType<T, K>
    ): ITypedControl<ArrayItemType<T, K>> => {
      const arrayChildren = getControlArray(fieldName);
      const controlsInRow = arrayChildren[rowIndex];
      return controlsInRow.filter(
        (x) => x.name === itemFieldName
      )?.[0] as ITypedControl<ArrayItemType<T, K>>;
    },
    [getControlArray]
  );

  /**
   * Handles form submission with validation
   * @param callback - Function to execute with form data if validation passes
   * @returns Promise that resolves after submission attempt
   */
  const submitHandler = useCallback(
    async (callback: (data: T) => Promise<void>) => {
      await instanceForm.submit<T>(callback);
    },
    [instanceForm]
  );

  /**
   * Updates default values for all form controls
   * @param defaults - New default values to set
   */
  const setDefaults = useCallback(
    (defaults?: T) => {
      instanceForm.setDefaults(defaults);
    },
    [instanceForm]
  );

  return {
    /** Form state and methods */
    form: {
      /** Validates all form controls */
      validate: instanceForm.validate,
      /** Overall form validation status */
      isValid: instanceForm.isValid,
      /** Overall form dirty status */
      isDirty: instanceForm.isDirty,
      /** Array of all form controls */
      controls: instanceForm.controlAsArray,
      /** Resets form to default values */
      reset: instanceForm.reset,
    },
    /** Get individual control by name */
    getControl,
    /** Get array control children with improved typing */
    getControlArray,
    /** Get specific array item control with proper typing */
    getArrayItemControl,
    /** Update default values */
    setDefaults,
    /** Submit form with callback */
    submitHandler,
    /** Current default values */
    defaults,
  };
}
